import { AuthConfig } from 'angular-oauth2-oidc-codeflow';
import { ENVIRONMENT } from '../../../../../environments/environment';
import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';

@Injectable()
export class ConfigServiceProvider {
  apiBaseUrl: string = ENVIRONMENT.apiBaseUrl;
  engineUrl: string = ENVIRONMENT.engineUrl;
  api = {
    registerDevice: 'api/users/register',
    account: 'api/account',

    resetPassword: 'api/account/reset_password/init',

    text: 'config/text',
    screens: 'config/screens',
    layout: 'config/layout',
    contestRoutes: 'api/config/contestRoutes',
    userGroupsConfig: 'api/config/userGroupsConfig',
    userGroupMembers: 'api/user-group-members',
    nonWorkingDays: 'api/config/nonWorkingDays',

    geoPoints: 'api/data/geopoints',
    geoPointsDefinitions: 'api/data/type-defs',

    player: 'api/',
    passwordChange: 'api/account/change_password',
    photoChange: 'api/users/change-photo',
    users: 'api/users',
    groups: 'api/user-groups',

    news: 'api/news',
    newsQuick: 'api/news/quick',

    attractivenessDict: 'api/config/attractivenessDict',

    rankingDefinitions: 'api/ranking-definitions',
    rankings: 'api/rankings',

    history: 'api/rankings/',

    kpiDefinitions: 'api/kpi-definitions',

    prizes: 'api/prizes',
    prizeClaims: 'api/prizeClaims',

    quizzes: 'api/quiz',
    quizDetails: 'api/quiz/',
    quizStart: 'api/quiz/start/',
    quizResults: 'api/quiz/results',
    quizSolve: 'api/quiz/solve',

    knowledge: 'api/knowledge',

    tasks: 'api/tasks',
    tasksContest: 'api/tasks-contest',

    achievements: 'api/achievements',

    client: 'api/users/client',
    clientEvents: 'api/client/{clientId}/events',
    userChildren: 'api/users/children',
    userIdChildren: 'api/users/{userId}/children',
    clients: 'api/clients',
    question: 'api/question',
    questionActive: 'api/question',
    eventsGet: 'api/events',
    eventsAdd: 'api/events/withClient',
    eventsEdit: 'api/events/{eventId}',
    synchronize: 'api/events/ms/synchronize',
    clientsAsArray: 'api/clients?format=array',
    audit: 'api/audit',
    auditClient: 'api/client/{clientId}/audits',
    auditDetail: 'api/audit/{auditId}',
    questionClient: 'api/question/client/{clientId}',
    questionAudit: 'api/question/audit/{auditId}',

    clientForm: 'api/client',
    contactForm: 'api/client/contact',
    updateContact: 'api/client/contact/{contactPersonId}',
    deleteContact: 'api/client/contact/{contactPersonId}',
    clientStrategy: 'api/client/{id}/strategy',
    clientStrategyEdit: 'api/client/strategy/{id}',
    clientResults: 'api/client/{id}/results',

    visitTaskQuery: 'api/visit-tasks/available',
    visitTaskEnd: 'api/visit-tasks/end-visit',
    visitTaskDone: 'api/visit-tasks',
    taskFormDefinition: 'api/process/create_representative_task/formDefinition/',
    endTask: 'api/tasks/{taskId}',
    createTask: 'api/process/create_representative_task/start',
    createProjectFormDefinition: 'api/process/create_project/formDefinition/',
    modifyProjectFormDefinition: 'api/process/modify_project/formDefinition/',
    createProject: 'api/process/create_project/start',
    modifyProject: 'api/process/modify_project/start',

    userTask: 'api/user-tasks/{id}',
    completeUserTask: 'api/tasks/{id}/complete',
    claimUserTask: 'api/tasks/{id}/claim',
  };
  AUTH_REALM = ENVIRONMENT.authRealm; // keycloak realm name
  APPLICATION_LOGIN = '#/login';
  ISSUER_LOGIN = ENVIRONMENT.authServerBaseUrl + 'auth/realms/' + this.AUTH_REALM;
  AUTH_SERVER_BASE_URL = ENVIRONMENT.authServerBaseUrl;
  AUTH_CLIENT_ID = ENVIRONMENT.authClientId; // keycloak client name
  AUTH_CLIENT_SECRET = ENVIRONMENT.authSecret; // keycloak client secret
  AUTH_LOGOUT_REDIRECT = 'https://localhost:8100/logoutCallback'; // must be keycloak allowed redirect
  AUTH_TOKEN_REDIRECT = ENVIRONMENT.authLoginRedirect; // must be keycloak allowed redirect
  LOGIN_REALM_URL = this.AUTH_SERVER_BASE_URL + 'auth/realms/' + this.AUTH_REALM + '/protocol/openid-connect';
  LOGIN_URL = this.LOGIN_REALM_URL + '/auth';
  TOKEN_URL = this.LOGIN_REALM_URL + '/token';
  LOGOUT_URL = this.LOGIN_REALM_URL + '/logout';
  LOGIN_FULL_URL: string;
  LOGOUT_FULL_URL: string;
  public USER_CREDS_FLOW_ENABLED = ENVIRONMENT.userCredsAuthEnabled || false; // do not set true for production
  config = {
    application: ENVIRONMENT.application,
    clientId: this.AUTH_CLIENT_ID,
    clientSecret: this.AUTH_CLIENT_SECRET,
    //TODO: remember to change versionUrl, application and app ID before releasing a verion for a new customer!!!
    versionUrl: ENVIRONMENT.versionUrl,
    version: ENVIRONMENT.versionWeb,

    openUrls: [this.TOKEN_URL, 'https://www.visuu.com/sellions/demo', this.ISSUER_LOGIN],

    profilePhotoPlaceholder: '/assets/images/profile_placeholder.png',
  };

  authConfig: AuthConfig = {
    issuer: this.ISSUER_LOGIN,
    redirectUri: ENVIRONMENT.authLoginRedirect,
    clientId: ENVIRONMENT.authClientId,
    scope: 'openid',
    responseType: 'code',
    disableAtHashCheck: true,
    showDebugInformation: true,
    strictDiscoveryDocumentValidation: false,
  };

  constructor(private plt: Platform) {
    this.buildLoginFullUrl();
    this.buildLogoutFullUrl();
    this.api['token'] = this.TOKEN_URL;

    if (this.plt.is('ios')) {
      this.config.version = ENVIRONMENT.versionIOS;
    } else if (this.plt.is('android')) {
      this.config.version = ENVIRONMENT.versionAndroid;
    }
  }

  getAuthorizationCodeFlow(): AuthConfig {
    return this.authConfig;
  }

  getUrl(identifier: string): string {
    if (this.api[identifier]) {
      return this.apiBaseUrl + this.api[identifier];
    }
    return null;
  }

  getEngineUrl(identifier: string): string {
    if (this.api[identifier]) {
      return this.engineUrl + this.api[identifier];
    }
    return null;
  }

  getConfig(identifier: string): any {
    return this.config[identifier] || null;
  }

  private buildLoginFullUrl() {
    let params = new URLSearchParams();
    params.append('client_id', this.AUTH_CLIENT_ID);
    params.append('redirect_uri', ENVIRONMENT.authLoginRedirect);
    params.append('response_type', 'code');
    params.append('scope', 'openid');

    this.LOGIN_FULL_URL = this.LOGIN_URL + '?' + params.toString();
  }

  private buildLogoutFullUrl() {
    let params = new URLSearchParams();
    params.append('redirect_uri', this.AUTH_LOGOUT_REDIRECT);

    this.LOGOUT_FULL_URL = this.LOGOUT_URL + '?' + params.toString();
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/services/config-service/config-service.ts